import { Component, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { DynamicApiService, DynamicEntity } from '../dynamic-api.service';
import {
  DYNAMIC_FIELD_SEMANTIC_TYPE_CATALOG,
  DYNAMIC_FIELD_TYPE_CATALOG,
  DYNAMIC_RELATIONSHIP_TYPE_CATALOG,
  DynamicFieldSemanticType,
  DynamicFieldType
} from '../dynamic.models';
import {
  EntityDraft,
  EntityDraftPayload,
  EntityFieldDraft,
  EntityFieldPayload,
  EntityIndexDraft,
  EntityRelationshipDraft
} from './ai-authoring.models';
import {
  hasAiGeneratePermission,
  resolveAiAuthoringError
} from './ai-authoring.util';

type SelectOption<T = string> = { label: string; value: T };

@Component({
  standalone: true,
  selector: 'ain-entity-draft-preview',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    ConfirmDialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    @if (draft(); as currentDraft) {
    <section class="entity-draft-preview">
      <ain-info-hint
        [titleKey]="'dynamic.aiAuthoring.entityPreview.infoTitle'"
        [descriptionKey]="'dynamic.aiAuthoring.entityPreview.infoDescription'"
        [bulletKeys]="[
          'dynamic.aiAuthoring.entityPreview.infoBulletFields',
          'dynamic.aiAuthoring.entityPreview.infoBulletRelationships',
          'dynamic.aiAuthoring.entityPreview.infoBulletApply'
        ]" />

      @if (usedFallback()) {
        <div class="banner banner-warn">
          <strong>{{ t('dynamic.aiAuthoring.fallbackDisclaimer') }}</strong>
        </div>
      }

      @if (warnings().length) {
        <div class="banner banner-info">
          <strong>{{ t('dynamic.aiAuthoring.warningsTitle') }}</strong>
          <ul>
            @for (warning of warnings(); track warning) {
              <li>{{ warning }}</li>
            }
          </ul>
        </div>
      }

      <div class="meta-grid">
        <ain-form-field [label]="t('dynamic.designer.logicalName')" [hint]="t('dynamic.entities.namePatternHint')">
          <input pInputText [(ngModel)]="currentDraft.name" />
        </ain-form-field>
        <ain-form-field [label]="t('columns.displayName')">
          <input pInputText [(ngModel)]="currentDraft.displayName" />
        </ain-form-field>
        <ain-form-field [label]="t('dynamic.entities.descriptionField')">
          <textarea pTextarea rows="2" [(ngModel)]="currentDraft.description"></textarea>
        </ain-form-field>
        <ain-form-field [label]="t('dynamic.designer.iconKey')">
          <input pInputText [(ngModel)]="currentDraft.iconKey" />
        </ain-form-field>
      </div>

      <div class="section-header">
        <h3>{{ t('dynamic.aiAuthoring.entityPreview.fieldsTitle') }}</h3>
        <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.field.add')" (click)="addField()"></button>
      </div>

      <p-table [value]="currentDraft.fields" dataKey="name">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('dynamic.field.displayName') }}</th>
            <th>{{ t('dynamic.field.name') }}</th>
            <th>{{ t('dynamic.field.type') }}</th>
            <th>{{ t('dynamic.field.semanticType') }}</th>
            <th>{{ t('dynamic.field.isRequired') }}</th>
            <th>{{ t('dynamic.field.isUnique') }}</th>
            <th>{{ t('dynamic.field.isIndexed') }}</th>
            <th style="width: 6rem"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-field let-rowIndex="rowIndex">
          <tr>
            <td><input pInputText class="cell-input" [(ngModel)]="field.displayName" [name]="'display-' + rowIndex" /></td>
            <td><input pInputText class="cell-input" [(ngModel)]="field.name" [name]="'name-' + rowIndex" /></td>
            <td>
              <p-select
                class="cell-select"
                [options]="fieldTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="field.fieldType"
                [name]="'type-' + rowIndex"
                appendTo="body" />
            </td>
            <td>
              <p-select
                class="cell-select"
                [options]="semanticTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="field.semanticType"
                [showClear]="true"
                [name]="'semantic-' + rowIndex"
                appendTo="body" />
            </td>
            <td><p-checkbox binary="true" [(ngModel)]="field.isRequired" [name]="'req-' + rowIndex" /></td>
            <td><p-checkbox binary="true" [(ngModel)]="field.isUnique" [name]="'uniq-' + rowIndex" /></td>
            <td><p-checkbox binary="true" [(ngModel)]="field.isIndexed" [name]="'idx-' + rowIndex" /></td>
            <td>
              <button pButton type="button" text severity="danger" icon="pi pi-trash" (click)="removeField(rowIndex)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (currentDraft.relationships.length) {
        <div class="section-header">
          <h3>{{ t('dynamic.aiAuthoring.entityPreview.relationshipsTitle') }}</h3>
        </div>
        <p-table [value]="currentDraft.relationships" dataKey="targetEntityKey">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 3rem">{{ t('dynamic.aiAuthoring.include') }}</th>
              <th>{{ t('dynamic.relationship.targetEntity') }}</th>
              <th>{{ t('dynamic.relationship.type') }}</th>
              <th>{{ t('dynamic.relationship.foreignKey') }}</th>
              <th>{{ t('columns.displayName') }}</th>
              <th>{{ t('dynamic.relationship.isRequired') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-rel>
            <tr>
              <td><p-checkbox binary="true" [(ngModel)]="rel.include" /></td>
              <td>
                <p-select
                  [options]="entityOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="rel.targetEntityKey"
                  [filter]="true"
                  appendTo="body" />
              </td>
              <td>
                <p-select
                  [options]="relationshipTypeOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="rel.relationshipType"
                  appendTo="body" />
              </td>
              <td><input pInputText [(ngModel)]="rel.foreignKeyFieldName" /></td>
              <td><input pInputText [(ngModel)]="rel.displayName" /></td>
              <td><p-checkbox binary="true" [(ngModel)]="rel.isRequired" /></td>
            </tr>
          </ng-template>
        </p-table>
      }

      @if (currentDraft.indexes.length) {
        <div class="section-header">
          <h3>{{ t('dynamic.aiAuthoring.entityPreview.indexesTitle') }}</h3>
        </div>
        <p-table [value]="currentDraft.indexes" dataKey="name">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 3rem">{{ t('dynamic.aiAuthoring.include') }}</th>
              <th>{{ t('dynamic.index.name') }}</th>
              <th>{{ t('dynamic.index.columns') }}</th>
              <th>{{ t('dynamic.index.isUnique') }}</th>
              <th>{{ t('dynamic.index.filterExpression') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-index>
            <tr>
              <td><p-checkbox binary="true" [(ngModel)]="index.include" /></td>
              <td><input pInputText [(ngModel)]="index.name" /></td>
              <td><input pInputText [(ngModel)]="index.columnsText" /></td>
              <td><p-checkbox binary="true" [(ngModel)]="index.isUnique" /></td>
              <td><input pInputText [(ngModel)]="index.filterExpression" /></td>
            </tr>
          </ng-template>
        </p-table>
      }

      <div class="footer-actions">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="cancelled.emit()"></button>
        @if (draftId()) {
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('dynamic.aiAuthoring.discard')"
            [disabled]="discarding()"
            (click)="discardDraft()"></button>
        }
        @if (canApply()) {
          <button
            pButton
            type="button"
            icon="pi pi-check"
            [label]="t('dynamic.aiAuthoring.apply')"
            [disabled]="applying()"
            (click)="confirmApply()"></button>
        }
      </div>

      <p-confirmDialog />
    </section>
    }
  `,
  styles: [`
    .entity-draft-preview { display: grid; gap: 16px; }
    .banner {
      border-radius: 10px;
      padding: 12px 14px;
      display: grid;
      gap: 6px;
    }
    .banner-warn { background: #fff7ed; border: 1px solid #fdba74; color: #9a3412; }
    .banner-info { background: #eff6ff; border: 1px solid #93c5fd; color: #1e3a8a; }
    .banner ul { margin: 0; padding-inline-start: 1.2rem; }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
    }
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }
    .section-header h3 { margin: 0; }
    .cell-input, .cell-select { width: 100%; min-width: 7rem; }
    .footer-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class EntityDraftPreviewComponent {
  private readonly api = inject(DynamicApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly draft = model<EntityDraft | null>(null);
  readonly warnings = input<string[]>([]);
  readonly usedFallback = input(false);
  readonly draftId = input<string | null>(null);

  readonly applied = output<string>();
  readonly cancelled = output<void>();
  readonly discarded = output<void>();

  readonly applying = signal(false);
  readonly discarding = signal(false);
  readonly entities = signal<DynamicEntity[]>([]);

  readonly fieldTypeOptions = signal<SelectOption<DynamicFieldType>[]>([]);
  readonly semanticTypeOptions = signal<SelectOption<DynamicFieldSemanticType | null>[]>([]);
  readonly relationshipTypeOptions = signal<SelectOption[]>([]);
  readonly entityOptions = signal<SelectOption[]>([]);

  constructor() {
    this.fieldTypeOptions.set(
      DYNAMIC_FIELD_TYPE_CATALOG.map(item => ({
        label: this.localization.translate(item.labelKey),
        value: item.value
      }))
    );
    this.semanticTypeOptions.set(
      DYNAMIC_FIELD_SEMANTIC_TYPE_CATALOG.map(item => ({
        label: this.localization.translate(item.labelKey),
        value: item.value
      }))
    );
    this.relationshipTypeOptions.set(
      DYNAMIC_RELATIONSHIP_TYPE_CATALOG.map(item => ({
        label: this.localization.translate(item.labelKey),
        value: item.value
      }))
    );
    this.loadEntities();
  }

  canApply(): boolean {
    const context = this.contextService.context();
    if (!hasAiGeneratePermission(context)) {
      return false;
    }
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }
    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.entities');
    return (node?.actions ?? []).some(action => action.toLowerCase() === 'create');
  }

  addField(): void {
    const current = this.draft();
    if (!current) {
      return;
    }
    const draft = { ...current, fields: [...current.fields] };
    draft.fields.push(this.emptyField(draft.fields.length + 1));
    this.draft.set(draft);
  }

  removeField(index: number): void {
    const current = this.draft();
    if (!current) {
      return;
    }
    const draft = { ...current, fields: [...current.fields] };
    draft.fields.splice(index, 1);
    this.draft.set(draft);
  }

  confirmApply(): void {
    const current = this.draft();
    if (!current) {
      return;
    }
    const name = current.name.trim();
    if (!name) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.designer.logicalName') });
      return;
    }

    if (!current.fields.length) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.aiAuthoring.entityPreview.noFields') });
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.aiAuthoring.applyConfirm.title'),
      message: this.t('dynamic.aiAuthoring.applyConfirm.entityMessage'),
      accept: () => this.applyDraft()
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  discardDraft(): void {
    const id = this.draftId();
    if (!id) {
      return;
    }

    this.discarding.set(true);
    this.api.discardAiDraft(id).pipe(
      finalize(() => this.discarding.set(false))
    ).subscribe({
      next: () => this.discarded.emit(),
      error: (error: unknown) => {
        this.messages.add({
          severity: 'error',
          summary: resolveAiAuthoringError(error, key => this.t(key))
        });
      }
    });
  }

  private loadEntities(): void {
    this.api.listEntities().subscribe({
      next: items => {
        this.entities.set(items ?? []);
        this.entityOptions.set(
          (items ?? []).map(entity => ({
            label: `${entity.displayName} (${entity.name})`,
            value: entity.name
          }))
        );
      }
    });
  }

  private applyDraft(): void {
    const draft = this.draft();
    if (!draft) {
      return;
    }

    this.applying.set(true);
    this.api.applyEntityDraft({
      draft: this.buildApplyDraftPayload(draft),
      draftId: this.draftId()
    }).pipe(
      finalize(() => this.applying.set(false))
    ).subscribe({
      next: response => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.aiAuthoring.applySuccess') });
        this.applied.emit(response.entityId);
        void this.router.navigate(['/dynamic/entities', response.entityId]);
      },
      error: (error: unknown) => {
        this.messages.add({
          severity: 'error',
          summary: resolveAiAuthoringError(error, key => this.t(key))
        });
      }
    });
  }

  private buildApplyDraftPayload(draft: EntityDraft): EntityDraftPayload {
    return {
      name: draft.name.trim(),
      displayName: draft.displayName.trim() || draft.name.trim(),
      description: draft.description?.trim() || null,
      iconKey: draft.iconKey?.trim() || null,
      fields: draft.fields.map(field => this.mapFieldForApply(field)),
      relationships: draft.relationships
        .filter(rel => rel.include)
        .map(rel => ({
          targetEntityKey: rel.targetEntityKey.trim(),
          relationshipType: rel.relationshipType,
          foreignKeyFieldName: rel.foreignKeyFieldName?.trim() || null,
          displayName: rel.displayName.trim(),
          isRequired: rel.isRequired
        })),
      indexes: draft.indexes
        .filter(index => index.include)
        .map(index => ({
          name: index.name.trim(),
          columns: this.resolveIndexColumns(index),
          isUnique: index.isUnique,
          filterExpression: index.filterExpression?.trim() || null
        }))
    };
  }

  private mapFieldForApply(field: EntityFieldDraft): EntityFieldPayload {
    return {
      name: field.name.trim(),
      displayName: field.displayName.trim() || field.name.trim(),
      fieldType: field.fieldType,
      semanticType: field.semanticType?.trim() || null,
      isRequired: field.isRequired,
      isUnique: field.isUnique,
      isIndexed: field.isIndexed,
      options: field.options?.length
        ? field.options.map(option => ({
            value: option.value.trim(),
            label: option.label.trim() || option.value.trim()
          }))
        : null,
      numericMin: field.numericMin ?? null,
      numericMax: field.numericMax ?? null,
      dateMin: field.dateMin ?? null,
      dateMax: field.dateMax ?? null,
      unit: field.unit?.trim() || null,
      description: field.description?.trim() || null
    };
  }

  private resolveIndexColumns(index: EntityIndexDraft): string[] {
    if (index.columnsText?.trim()) {
      return index.columnsText
        .split(',')
        .map(column => column.trim())
        .filter(column => column.length > 0);
    }

    return index.columns
      .map(column => column.trim())
      .filter(column => column.length > 0);
  }

  private emptyField(index: number): EntityFieldDraft {
    return {
      name: `field_${index}`,
      displayName: `Field ${index}`,
      fieldType: 'Text',
      semanticType: 'Text',
      isRequired: false,
      isUnique: false,
      isIndexed: false,
      options: []
    };
  }
}
